import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./ApiKeyScreen.css";

// Provider logos (replace with your actual logos in /assets)
import groqLogo from "../../../assets/groq.png";
import openaiLogo from "../../../assets/openai.png";
import togetherLogo from "../../../assets/together.png";
import fireworksLogo from "../../../assets/fireworks.png";
import mistralLogo from "../../../assets/mistral.png";
import openrouterLogo from "../../../assets/openrouter.png";
export default function ApiKeyForm() {
  const [apiKey, setApiKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [provider, setProvider] = useState("groq");
  const [model, setModel] = useState("");
  const [temperature, setTemperature] = useState(parseFloat(process.env.REACT_APP_LLM_TEMP) || 0.2);
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/";
  const [showInstructions, setShowInstructions] = useState(false);
  const token = localStorage.getItem("token");
  const userId = token ? JSON.parse(atob(token.split(".")[1])).id : null;

  // const providerModels = {
  //   groq: ["llama-3.3-70b-versatile","llama-3.1-8b-instant","groq/compound","groq/compound-mini"],
  //   openrouter: ["qwen/qwen3.6-plus","arcee-ai/trinity-large-preview:free","z-ai/glm-4.5-air:free"],
  //   openai: ["gpt-4o-mini","o4-mini","gpt-4o"],
  //   together: ["togethercomputer/mixtral-8x7b-instruct","togethercomputer/llama-2-70b-chat","togethercomputer/llama-3.2-11b-free"],
  //   fireworks: ["accounts/fireworks/models/qwen2p5-vl-7b-instruct","accounts/fireworks/models/deepseek-r1","accounts/fireworks/models/gpt-oss-20b","accounts/fireworks/models/qwen3-1p7b-fp8-draft-131072","accounts/fireworks/models/llama-v3p1-405b-instruct","accounts/fireworks/models/llama-v3p1-8b-instruct","accounts/fireworks/models/mixtral-8x7b-instruct"],
  //   mistral: ["open-mistral-7b","mistral-large","mistral-medium","mistral-small"],
  // };
  const providerModels = {
    groq: JSON.parse(process.env.REACT_APP_GROQ_MODELS || "[]"),
    openrouter: JSON.parse(process.env.REACT_APP_OPENROUTER_MODELS || "[]"),
    openai: JSON.parse(process.env.REACT_APP_OPENAI_MODELS || "[]"),
    together: JSON.parse(process.env.REACT_APP_TOGETHER_MODELS || "[]"),
    fireworks: JSON.parse(process.env.REACT_APP_FIREWORKS_MODELS || "[]"),
    mistral: JSON.parse(process.env.REACT_APP_MISTRAL_MODELS || "[]")
  };
  const providerLinks = {
    openrouter:"https://openrouter.ai/workspaces/default/keys",
    groq: "https://console.groq.com/keys",
    openai: "https://platform.openai.com/account/api-keys",
    together: "https://api.together.xyz/settings/keys",
    fireworks: "https://fireworks.ai/account/api-keys",
    mistral: "https://console.mistral.ai/api-keys",
  };

  const providerLogos = {
    groq: groqLogo,
    openrouter: openrouterLogo,
    openai: openaiLogo,
    together: togetherLogo,
    fireworks: fireworksLogo,
    mistral: mistralLogo,
  };

  const providerUrls = {
    openrouter:"https://openrouter.ai/api/v1",
    groq: "https://api.groq.com/openai/v1",
    openai: "https://api.openai.com/v1",
    together: "https://api.together.xyz/v1",
    fireworks: "https://api.fireworks.ai/inference/v1",
    mistral: "https://api.mistral.ai/v1",
  };

  useEffect(() => {
    const models = providerModels[provider] || [];
    // const llamaModel = models.find((m) => m.toLowerCase().includes("llama"));
    setModel(models[0] || "");
  }, [provider]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!apiKey.trim()) return setError("API key is required");
    if (!userId || !token) return setError("Not authenticated. Please log in again.");
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND}/user/save_api_key/${userId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          apikey: apiKey,
          provider_name: provider,
          model_name: model,
          provider_url: providerUrls[provider],
          temperature: temperature,
        }),
      });

      if (!res.ok) {
        const errMsg = await res.text();
        throw new Error(errMsg || "Failed to save API key");
      }
      navigate(redirectTo, { replace: true });
    } catch (err) {
      alert(JSON.parse(err.message).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="api-page">
      <div className="api-card">
        <h1 className="api-title">🔑 Connect Your API Key</h1>
        <p className="api-desc">
          Select a provider, choose a model, and paste your API key below. Your keys are securely stored and never shared.
        </p>
        <div className="instructions-container">
          <button
            type="button"
            onClick={() => setShowInstructions(!showInstructions)}
            className="instructions-toggle"
          >
            {showInstructions ? "🔽 Hide Instructions" : "ℹ️ Show Instructions"}
          </button>

          {showInstructions && (
            <div className="api-instructions">
              <h3 className="instructions-title">What is an API Key?</h3>
              <p>
                An <strong>API key</strong> is like a digital pass that allows
                this app to connect to AI services such as{" "}
                <em>OpenAI</em>.
              </p>
              <p>
                You need to enter it so the app can generate answers, summaries,
                or validate ideas using your selected AI model.
                temperature settings help control the creativity of the AI responses.
                set it to 0 for more focused answers, or higher values for more creative outputs.
              </p>
              <ol className="instruction-steps">
                <li>1️⃣ Choose your <strong>provider</strong> (e.g., OpenAI).</li>
                <li>2️⃣ Click the link below (<strong>Get your key</strong>).</li>
                <li>3️⃣ Copy your key.</li>
                <li>4️⃣ Paste it into the box below.</li>
                <li>✅ Click <strong>Verify & Save</strong>.</li>
              </ol>
              <p className="instructions-note">
                🔒 Your key is stored securely and used only for your requests.
              </p>
            </div>
          )}
        </div>
        <br />
        {error && <div className="api-error">{error}</div>}

        <form onSubmit={handleSubmit} className="api-form">
          {/* Provider */}
          <label className="api-label">
            Provider
            <div className="provider-select-wrapper">
              <select
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
                className="api-select"
              >
                {Object.keys(providerModels).map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              <img src={providerLogos[provider]} alt={provider} className="provider-logo" />
            </div>
          </label>

          {/* Model */}
          <label className="api-label">
            Model
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="api-select"
            >
              {providerModels[provider].map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </label>

          {/* Temperature */}
          <label className="api-label">
            Temperature
            <input
              type="number"
              step="0.1"
              min="0"
              max="1"
              value={temperature}
              onChange={(e) => setTemperature(parseFloat(e.target.value))}
              className="api-input"
            />
          </label>

          {/* API Key */}
          <label className="api-label">
            <h7>API Key<span>*</span></h7>
            <div className="input-wrapper">
              <input
                type={showKey ? "text" : "password"}
                placeholder="sk-..."
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="api-input"
                disabled={loading}
                required
              />
              <button type="button" onClick={() => setShowKey(!showKey)} className="show-btn">
                {showKey ? "Hide" : "Show"}
              </button>
            </div>
          </label>

          <p className="api-inf">
            👉 Get your <span className="font-semibold">{provider}</span> key{" "}
            <a href={providerLinks[provider]} target="_blank" rel="noreferrer" className="api-link">
              here
            </a>.
          </p>

          <button type="submit" disabled={loading} className="api-submit">
            {loading ? "Saving..." : "Verify & Save"}
          </button>
        </form>
      </div>
    </div>
  );
}
